import { useId, useState } from 'react';
import { Button, chipClass, DialogShell, fieldClass, labelClass } from '@timely/ui';
import {
    TEMPLATE_VARIABLES, renderTemplate, type TemplateChannel, type TemplateContext,
} from '@timely/domain';
import { FormNotice } from '../screens/AuthLayout';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { useFormatters } from '../book/useFormatters';
import {
    TemplateError, createTemplate, updateTemplate,
    type MessageTemplate, type TemplateDraft,
} from './useTemplates';

const CHANNEL_LABEL: Record<TemplateChannel, string> = {
    whatsapp: 'WhatsApp', email: 'Email', internal: 'Internal note',
};

/**
 * Always available, never a real account — preview never touches real data. The customer is invented; the
 * amounts are in this company's own currency and format, and the company is this one (2026-10 global sweep: an
 * Indian company's preview read "£12,400" from "Northwind Traders").
 */
function sampleContext(company: string, money: (amount: number) => string): TemplateContext {
    return {
        customer_name: 'Wilson Brothers',
        outstanding_amount: money(12400),
        company_name: company,
        follow_up_date: '12 Oct',
        expected_amount: money(5000),
        contact_name: 'Priya',
    };
}

export function TemplateDialog({
    workspaceId, template, onClose, onSaved,
}: {
    workspaceId: string;
    template: MessageTemplate | null;
    onClose: () => void;
    onSaved: (message: string) => void | Promise<void>;
}) {
    const field = useId();
    const [draft, setDraft] = useState<TemplateDraft>({
        name: template?.name ?? '',
        content: template?.content ?? '',
        channel: template?.channel ?? 'whatsapp',
    });
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const insertVariable = (key: string) => {
        setDraft(d => ({ ...d, content: `${d.content}{{${key}}}` }));
    };

    const save = async () => {
        setSaving(true);
        setProblem(null);
        try {
            if (template) {
                await updateTemplate(template.id, draft);
            } else {
                await createTemplate(workspaceId, draft);
            }
            await onSaved(template ? 'Template updated.' : 'Template created.');
        } catch (err) {
            setProblem(err instanceof TemplateError ? err.message : 'That did not work.');
        } finally {
            setSaving(false);
        }
    };

    const { workspace } = useActiveWorkspace();
    const { money } = useFormatters();
    const preview = renderTemplate(draft.content, sampleContext(workspace.name, n => money.exact(n)), draft.channel);

    return (
        <DialogShell
            title={template ? 'Edit template' : 'New template'}
            subtitle="Everyone in this company can use it. No other company can see it."
            size="lg"
            onClose={onClose}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button variant="primary" onClick={() => { void save(); }} disabled={saving} busy={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </>
            }
        >
            {problem && <div className="mb-4"><FormNotice tone="error">{problem}</FormNotice></div>}

            <label htmlFor={`${field}-name`} className={labelClass}>Name</label>
            <input
                id={`${field}-name`}
                type="text" value={draft.name} maxLength={80}
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                className={fieldClass({ className: 'w-full mb-4' })}
            />

            <label htmlFor={`${field}-channel`} className={labelClass}>Channel</label>
            <select
                id={`${field}-channel`}
                value={draft.channel}
                onChange={e => setDraft(d => ({ ...d, channel: e.target.value as TemplateChannel }))}
                className={fieldClass({ className: 'w-full mb-4' })}
            >
                {(Object.keys(CHANNEL_LABEL) as TemplateChannel[]).map(c => (
                    <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>
                ))}
            </select>

            <label htmlFor={`${field}-content`} className={labelClass}>Content</label>
            <textarea
                id={`${field}-content`}
                value={draft.content} rows={6}
                onChange={e => setDraft(d => ({ ...d, content: e.target.value }))}
                className={fieldClass({ multiline: true, className: 'w-full mb-2' })}
            />

            <div className="flex flex-wrap gap-1.5 mb-4">
                {TEMPLATE_VARIABLES.map(v => (
                    <button
                        key={v.key} type="button" title={v.description}
                        onClick={() => insertVariable(v.key)}
                        className={chipClass()}
                    >
                        + {v.label}
                    </button>
                ))}
            </div>

            <p className={labelClass}>Preview, with sample data</p>
            <div className="rounded-control bg-card-2 border border-separator px-3 py-2 text-body whitespace-pre-wrap min-h-12">
                {preview || <span className="text-label-3">Nothing to show yet.</span>}
            </div>
        </DialogShell>
    );
}
